import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { type Area, dataHealth } from '@/lib/data-health';
import { t, tn } from '@/lib/i18n';
import { useNetwork } from '@/lib/network';
import { useLineTimetables } from '@/lib/timetables';

const AREAS: Array<{ key: Area; label: string }> = [
  { key: 'lines', label: t('Lines') },
  { key: 'directions', label: t('Directions') },
  { key: 'stations', label: t('Stations') },
  { key: 'timetables', label: t('Timetables') },
];

/** Everything the network data checks find, in one list, each with a link to where it is fixed. */
export function DataHealthPage() {
  const network = useNetwork();
  const timetables = useLineTimetables();
  const [area, setArea] = useState<Area | 'all'>('all');
  const issues = useMemo(
    () => (network.data ? dataHealth(network.data, timetables.data?.items) : []),
    [network.data, timetables.data],
  );
  if (!network.data) return <Spinner />;
  const count = (a: Area) => issues.filter((i) => i.area === a);
  const shown = (area === 'all' ? issues : issues.filter((i) => i.area === area)).sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === 'problem' ? -1 : 1,
  );

  return (
    <>
      <PageHeader
        title={t('Data health')}
        subtitle={t(
          'Checks across lines, directions, stations and timetables. Problems affect what passengers see; the rest is worth a look.',
        )}
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {AREAS.map((a) => {
          const list = count(a.key);
          const problems = list.filter((i) => i.severity === 'problem').length;
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => setArea(area === a.key ? 'all' : a.key)}
              className={`rounded-lg border bg-white p-4 text-left shadow-sm ${area === a.key ? 'border-brand-600 ring-2 ring-brand-600/20' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <div className="text-sm text-slate-500">{a.label}</div>
              <div className="mt-1 flex items-center gap-2 text-2xl font-semibold">
                {list.length === 0 ? (
                  <>
                    <CircleCheck className="size-5 text-emerald-600" /> {t('All good')}
                  </>
                ) : (
                  list.length
                )}
              </div>
              {list.length > 0 && (
                <div className="text-xs text-slate-500">
                  {tn(problems, '{n} problem', '{n} problems')} ·{' '}
                  {tn(list.length - problems, '{n} to check', '{n} to check')}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <Card padded={false}>
        {shown.length === 0 ? (
          <p className="flex items-center gap-2 px-4 py-4 text-sm text-slate-600">
            <CircleCheck className="size-4 text-emerald-600" /> {t('Nothing found.')}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {shown.map((i, n) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: issues have no identity
              <li key={n} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                {i.severity === 'problem' ? (
                  <CircleAlert className="mt-0.5 size-4 shrink-0 text-red-600" aria-label={t('Problem')} />
                ) : (
                  <Info className="mt-0.5 size-4 shrink-0 text-amber-600" aria-label={t('To check')} />
                )}
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-slate-900">{i.subject}</span>
                  <span className="ml-2 text-xs text-slate-400">
                    {AREAS.find((a) => a.key === i.area)?.label}
                  </span>
                  <div className="text-slate-600">{i.text}</div>
                </div>
                <Link to={i.link} className="shrink-0 font-medium text-brand-700 hover:underline">
                  {t('Fix')} →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
